import React, { useCallback, useMemo } from 'react';
import { WidthInputWithUnitField } from '../../../components/Form';
import { useFocusIdx, useBlock } from '@bay-email/editor';
import { BasicType, getParentByIdx } from '@bay-email/core';
import { WidthInputWithUnitProps } from '@extensions/components/Form/WidthInputWithUnit';
import { UseFieldConfig } from 'react-final-form';
import { AutoCheckboxInput } from '../../../components/Form/AutoCheckboxInput';

export function Width({
	inline = false,
	unitOptions,
	config,
	placeholder,
	autoEnabled = false,
	maxPx,
	helpText,
	defaultUnit,
	showPlaceholderWhenAuto,
	allowAuto,
	disabled,
	autoChecked,
	onAutoToggle,
	onValueCommitted,
}: {
	inline?: boolean;
	unitOptions?: WidthInputWithUnitProps['unitOptions'];
	defaultUnit?: string;
	config?: UseFieldConfig<any>;
	placeholder?: string;
	autoEnabled?: boolean;
	maxPx?: number;
	helpText?: React.ReactNode;
	showPlaceholderWhenAuto?: boolean;
	allowAuto?: boolean;
	disabled?: boolean;
	autoChecked?: boolean;
	onAutoToggle?: (_checked: boolean) => void;
	onValueCommitted?: (_value: string | undefined) => void;
}) {
	const { focusIdx } = useFocusIdx();
	const { focusBlock, values } = useBlock();
	const parentType = getParentByIdx(values, focusIdx)?.type;

	const clampWidthToMaxPx = useCallback(
		(val: any) => {
			if (typeof maxPx !== 'number' || val === undefined || val === null) {
				return val;
			}

			const raw = val.toString().trim();
			if (!raw) {
				return val;
			}

			const pixelMatch = raw.match(/^(\d+(?:\.\d+)?)px$/i);
			const numberMatch = raw.match(/^(\d+(?:\.\d+)?)$/);
			if (!pixelMatch && !numberMatch) {
				return val;
			}

			const parsedNumber = Number(pixelMatch ? pixelMatch[1] : numberMatch?.[1]);
			if (Number.isNaN(parsedNumber)) {
				return val;
			}

			const clamped = Math.min(parsedNumber, maxPx);
			const clampedString = Number.isInteger(clamped)
				? clamped.toString()
				: clamped.toString();

			return pixelMatch ? `${clampedString}px` : clampedString;
		},
		[maxPx],
	);

	const mergedConfig = useMemo(() => {
		if (typeof maxPx !== 'number') {
			return config;
		}

		const baseParse = config?.parse;

		return {
			...config,
			parse: (value: any) => {
				const parsedValue = baseParse ? baseParse(value) : value;
				return clampWidthToMaxPx(parsedValue);
			},
		};
	}, [clampWidthToMaxPx, config, maxPx]);

	const widthLabel = unitOptions ? t('Width') : `${t('Width')} (px)`;

	const validate = useCallback(
		(val: string): string | undefined => {
			if (!val) {
				return undefined;
			}

			if (typeof maxPx === 'number') {
				const normalizedValue = val.toString().trim().toLowerCase();
				const pixelMatch = normalizedValue.match(/^(\d+(?:\.\d+)?)px$/);
				const numberMatch = normalizedValue.match(/^(\d+(?:\.\d+)?)$/);
				const pixelValue = pixelMatch
					? Number(pixelMatch[1])
					: numberMatch
						? Number(numberMatch[1])
						: null;

				if (pixelValue !== null && !Number.isNaN(pixelValue) && pixelValue > maxPx) {
					return t(`Width cannot exceed ${maxPx}px`);
				}
			}

			if (focusBlock?.type === BasicType.COLUMN && parentType === BasicType.GROUP) {
				return /(\d)*%/.test(val)
					? undefined
					: t('Column inside a group must have a width in percentage, not in pixel');
			}
			return undefined;
		},
		[focusBlock?.type, maxPx, parentType],
	);

	if (autoEnabled) {
		return (
			<AutoCheckboxInput
				validate={validate}
				label={`${t('Width')} (px)`}
				name={`${focusIdx}.attributes.width`}
				config={mergedConfig}
				autoValue='auto'
				placeholder={placeholder}
				showPlaceholderWhenAuto={showPlaceholderWhenAuto}
				allowAuto={allowAuto}
				disabled={disabled}
				autoChecked={autoChecked}
				onAutoToggle={onAutoToggle}
				onValueCommitted={onValueCommitted}
			/>
		);
	}

	return (
		<WidthInputWithUnitField
			validate={validate}
			label={widthLabel}
			inline={inline}
			name={`${focusIdx}.attributes.width`}
			unitOptions={unitOptions}
			defaultUnit={defaultUnit}
			config={mergedConfig}
			onChangeAdapter={clampWidthToMaxPx}
			placeholder={placeholder}
			helpText={helpText}
		/>
	);
}
